import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, OnInit, input, output, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  CouponService,
  ExcelService,
  OrganizationService,
} from '@fitness-force/api';
import { Status } from '@fitness-force/enum';
import type {
  OfferMaster,
  ListOrganization,
  Organization,
  OfferEligibleOrganization,
  AddOrganisationRequest,
} from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastMessageService } from '@fitness-force/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ScrollPanelModule } from 'primeng/scrollpanel';

import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'coupon-organization-restriction',
  templateUrl: './coupon-organization-restriction.component.html',
  standalone: true,
  imports: [
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
    ScrollPanelModule,
    ButtonModule,
    SelectModule,
    TranslateModule,
    AsyncPipe,
    NgTemplateOutlet,
  ],
})
export class CouponOrganizationRestrictionComponent implements OnInit {
  private coupnService = inject(CouponService);
  private translate = inject(TranslateService);
  private excelService = inject(ExcelService);
  private toastMessageService = inject(ToastMessageService);
  private organizationService = inject(OrganizationService);

  readonly tenantId = input(0);
  readonly couponMaster = input<OfferMaster>();
  readonly visible = input<boolean>(false);
  readonly organizationIds = input<number[]>([]);
  readonly closeEvent = output<any>({ alias: 'closeEvent' });

  organizationList: Organization[] = [];
  couponOrganization: OfferEligibleOrganization[] = [];
  selectedOrganization = new BehaviorSubject<OfferEligibleOrganization[]>([]);
  reloadList: boolean = false;

  ngOnInit(): void {
    this.BindOrganizations();
  }

  BindOrganizations() {
    this.organizationService.ListOrganization(this.tenantId()).subscribe({
      next: (res: ListOrganization) => {
        if (res.items)
          this.organizationList = res.items.filter(
            (value: any) =>
              value.status == Status.Active.toString() ||
              value.status == Status[Status.Active],
          );
        else this.organizationList = [];
        this.getCouponEligibleOrganization();
      },
      error: (error: any) => {},
    });
  }

  getCouponEligibleOrganization() {
    const couponMaster = this.couponMaster();
    if (!couponMaster) {
      return;
    }
    this.coupnService
      .getEligibleOrganization(this.tenantId(), couponMaster.id)
      .subscribe({
        next: (res: any) => {
          if (res.items) {
            this.couponOrganization = res.items as OfferEligibleOrganization[];
            this.couponOrganization.forEach((o) => {
              o.name_primary_language = this.organizationList.find(
                (f) => f.id == o.organisation_id,
              )!.name_primary_language;
            });
            this.selectedOrganization.next(this.couponOrganization);
          } else {
            this.selectedOrganization.next([]);
            this.couponOrganization = [];
          }
        },
        error: (error: any) => {},
      });
  }

  addOrganisations(event: any) {
    let payload: AddOrganisationRequest = {} as AddOrganisationRequest;
    payload.organisation_ids = [];
    payload.organisation_ids.push(event.value.id);
    const couponMaster = this.couponMaster();
    if (!couponMaster) {
      return;
    }
    this.coupnService
      .addEligibleOrganization(this.tenantId(), couponMaster.id, payload)
      .subscribe({
        next: (res: any) => {
          this.translate
            .get('ADMIN.COUPON.ERRORS.ORGANIZATION_ADDED')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          this.getCouponEligibleOrganization();
          this.reloadList = true;
        },
        error: (error: any) => {
          this.handleToastError(error);
        },
      });
  }

  removeOrganisation(offerEligibleOrganization: OfferEligibleOrganization) {
    if (this.couponOrganization && this.couponOrganization.length == 1) {
      this.translate
        .get('ADMIN.COUPON.ERRORS.ONE_ORGANIZATION_REQUIRED')
        .subscribe((translation) => {
          this.toastMessageService.showErrorMessage(translation);
        });
    } else {
      this.coupnService
        .removeEligibleOrganization(
          this.tenantId(),
          offerEligibleOrganization.offer_master_id,
          offerEligibleOrganization.id,
        )
        .subscribe({
          next: (res: any) => {
            this.translate
              .get('ADMIN.COUPON.ERRORS.ORGANIZATION_REMOVED')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.getCouponEligibleOrganization();
            this.reloadList = true;
          },
          error: (error: any) => {
            this.handleToastError(error);
          },
        });
    }
  }

  handleToastError(error: any) {
    this.toastMessageService.showError(error);
  }

  closePopup() {
    this.closeEvent.emit({ reload: this.reloadList });
    this.closeEvent.emit({ reload: this.reloadList });
  }
}
